import { createSelector } from 'reselect'
import moment from 'moment'
import { LabFeatureFlags } from 'constants/lab'
import { isFeatureTogglePayload, isFunnelConfigPayload } from 'lib/lab/dynamicPayload'
import getCheckoutTotal from './payment/getCheckoutTotal'
import getInsuranceDestinationCountriesFromCart from './view/getInsuranceDestinationCountriesFromCart'
import { getOccupantsFromCartItems } from './request/insurance'
import getInsuranceDatesFromCart from './request/getInsuranceDatesFromCart'
import { unique } from 'lib/array/arrayUtils'

/**
 * Parameters for building funnel cart attributes from offer page state.
 * Used before the cart is created to pre-fetch funnel features.
 */
export interface OfferPageFunnelParams {
  totalPrice: number
  destinationCountry?: string
  rooms: Array<App.RoomOccupants>
  checkIn?: string
  duration?: number
  offerId: string
  productType?: string
}

/**
 * Builds cart attributes for funnel optimiser from offer page state.
 * This allows pre-fetching funnel features BEFORE the cart is created,
 * enabling decisions like whether to show the upgrade modal.
 */
export function buildOfferPageCartAttributes(params: OfferPageFunnelParams): Record<string, unknown> {
  const { totalPrice, destinationCountry, rooms, checkIn, duration, offerId, productType = 'hotel' } = params

  const today = moment()
  const startDate = checkIn ? moment(checkIn) : null
  const leadTimeDays = startDate ? startDate.diff(today, 'days') : null
  const lengthOfStayNights = duration ?? null

  const numberOfAdults = rooms.reduce((sum, room) => sum + (room.adults ?? 0), 0)
  const numberOfChildren = rooms.reduce((sum, room) => sum + (room.children ?? 0) + (room.teenagers ?? 0), 0)
  const numberOfInfants = rooms.reduce((sum, room) => sum + (room.infants ?? 0), 0)
  const numberOfGuests = numberOfAdults + numberOfChildren + numberOfInfants

  return {
    cartTotalPrice: totalPrice,
    destinationCountries: destinationCountry ? [destinationCountry] : [],
    numberOfAdults,
    numberOfChildren,
    numberOfInfants,
    numberOfGuests,
    leadTimeDays,
    lengthOfStayNights,
    productTypes: [productType],
    offerIds: [offerId],
  }
}

export const DEFAULT_FUNNEL_FEATURES: App.FunnelFeatureToggles = {
  walledGarden: true,
  roomUpgrade: true,
  luxPlus: true,
  flights: true,
  experiences: true,
  cfmr: true,
  travelProtection: true,
  carHire: true,
}

/**
 * Extracts funnel feature toggles from a lab feature flag result.
 * Can be used both in selectors and in action creators to compute features from API responses.
 */
export function extractFunnelFeatures(funnelFlag: App.LabFeatureFlagResult | undefined): App.FunnelFeatureToggles {
  if (!funnelFlag?.enabled) {
    return DEFAULT_FUNNEL_FEATURES
  }

  const funnelToggles = funnelFlag.value

  if (!isFeatureTogglePayload(funnelToggles)) {
    return DEFAULT_FUNNEL_FEATURES
  }

  return {
    walledGarden: funnelToggles.toggles?.walledGarden ?? DEFAULT_FUNNEL_FEATURES.walledGarden,
    roomUpgrade: funnelToggles.toggles?.roomUpgrade ?? DEFAULT_FUNNEL_FEATURES.roomUpgrade,
    luxPlus: funnelToggles.toggles?.luxPlus ?? DEFAULT_FUNNEL_FEATURES.luxPlus,
    flights: funnelToggles.toggles?.flights ?? DEFAULT_FUNNEL_FEATURES.flights,
    experiences: funnelToggles.toggles?.experiences ?? DEFAULT_FUNNEL_FEATURES.experiences,
    cfmr: funnelToggles.toggles?.cfmr ?? DEFAULT_FUNNEL_FEATURES.cfmr,
    travelProtection: funnelToggles.toggles?.travelProtection ?? DEFAULT_FUNNEL_FEATURES.travelProtection,
    carHire: funnelToggles.toggles?.carHire ?? DEFAULT_FUNNEL_FEATURES.carHire,
  }
}

export const getFunnelFeatures = createSelector(
  (state: App.State) => state.system.features.labFlags[LabFeatureFlags.enableFunnelOptimiser],
  extractFunnelFeatures,
)

/**
 * Metadata about the funnel rule evaluation result.
 */
export interface FunnelEvaluationMetadata {
  /** ID of the funnel rule that was applied. Null if no rule matched or defaults were used. */
  ruleId: number | null
  /** Name of the funnel rule that was applied. Null if no rule matched or defaults were used. */
  ruleName: string | null
  /**
   * The outcome of funnel rule evaluation.
   * Use this single field to determine what happened:
   * - `matched`: A rule matched (check ruleId/ruleName)
   * - `default:*`: Defaults were used (check specific reason)
   */
  outcome: App.FunnelEvaluationOutcome
}

/** Default metadata when funnel optimiser is not enabled or payload is invalid */
const DEFAULT_FUNNEL_METADATA: FunnelEvaluationMetadata = {
  ruleId: null,
  ruleName: null,
  outcome: 'default:no_rules',
}

/**
 * Selector to get the funnel evaluation metadata (ruleId, outcome).
 * This can be used for debugging, analytics, or displaying which rule was applied.
 */
export const getFunnelEvaluationMetadata = createSelector(
  (state: App.State) => state.system.features.labFlags[LabFeatureFlags.enableFunnelOptimiser],
  (funnelFlag: App.LabFeatureFlagResult): FunnelEvaluationMetadata => {
    if (!funnelFlag?.enabled) {
      return DEFAULT_FUNNEL_METADATA
    }

    const funnelPayload = funnelFlag.value

    if (!isFunnelConfigPayload(funnelPayload)) {
      return DEFAULT_FUNNEL_METADATA
    }

    return {
      ruleId: funnelPayload.ruleId,
      ruleName: funnelPayload.ruleName,
      outcome: funnelPayload.outcome,
    }
  },
)

/**
 * Builds cart attributes for funnel optimiser evaluation.
 * These attributes are sent to svc-lab for audience targeting based on cart contents.
 */
export const buildFunnelCartAttributes = createSelector(
  (state: App.State) => getCheckoutTotal(state),
  (state: App.State) => getInsuranceDestinationCountriesFromCart(state),
  (state: App.State) => getOccupantsFromCartItems(state),
  (state: App.State) => getInsuranceDatesFromCart(state),
  (state: App.State) => state.checkout.cart.items,
  (totalPrice, destinations, occupancy, dates, items): Record<string, unknown> => {
    const today = moment()
    const startDate = dates?.startDate ? moment(dates.startDate) : null
    const endDate = dates?.endDate ? moment(dates.endDate) : null
    const leadTimeDays = startDate ? startDate.diff(today, 'days') : null
    const lengthOfStayNights = startDate && endDate ? endDate.diff(startDate, 'days') : null
    const productTypes = unique(items.map(item => item.itemType))
    const offerIds = unique(items.map(item => 'offerId' in item ? item.offerId : null).filter(Boolean))

    return {
      cartTotalPrice: totalPrice,
      destinationCountries: destinations,
      numberOfAdults: occupancy?.adults ?? 0,
      numberOfChildren: occupancy?.children ?? 0,
      numberOfInfants: occupancy?.infants ?? 0,
      numberOfGuests: (occupancy?.adults ?? 0) + (occupancy?.children ?? 0) + (occupancy?.infants ?? 0),
      leadTimeDays,
      lengthOfStayNights,
      productTypes,
      offerIds,
    }
  },
)
